// SECURITY: API keys are passed through this module but MUST NEVER be logged.
// Do not console.log() the request bodies that include `api_key`.
// Do not include keys in error messages, analytics events, or PostHog.

import { supabase } from '@/lib/supabaseClient';
import type {
  TestConnectionRequest,
  TestConnectionResponse,
  ConnectRequest,
  ConnectResponse,
  RotateKeyRequest,
  RotateKeyResponse,
  EnableDisableResponse,
  DeleteIntegrationResponse,
  ReconnectRequest,
  ReconnectResponse,
  SubContractorHubIntegration,
} from '@/types/integrations/subcontractorhub';

const BACKEND_URL =
  import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function authHeader(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

interface ApiEnvelope<TData> {
  data: TData | null;
  error: { code: string; message: string; details?: Record<string, unknown> } | null;
  meta: unknown;
}

async function _toggleEnabled(path: string): Promise<EnableDisableResponse> {
  const env = await postJson<RawEnableDisableData>(path, {});
  if (env.data) {
    return {
      data: {
        enabled: env.data.enabled,
        lastSyncStatus: env.data.last_sync_status,
      },
      error: null,
    };
  }
  if (env.error) {
    const knownCodes = ['integration_not_found', 'internal_error'] as const;
    type KnownCode = (typeof knownCodes)[number];
    const code = (knownCodes as readonly string[]).includes(env.error.code)
      ? (env.error.code as KnownCode)
      : 'internal_error';
    return {
      data: null,
      error: { code, message: env.error.message, details: env.error.details },
    };
  }
  return { data: null, error: { code: 'internal_error', message: 'Unknown error' } };
}

async function patchJson<TData>(path: string, body: unknown): Promise<ApiEnvelope<TData>> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

async function postJson<TData>(path: string, body: unknown): Promise<ApiEnvelope<TData>> {
  const headers = await authHeader();
  const response = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  // The backend returns the envelope shape with HTTP 200 even for
  // expected error codes (auth_failed, etc.). Only HTTP 4xx/5xx that
  // bypass our envelope (e.g. authentication failure on the Joules side)
  // need transport-level handling.
  if (!response.ok && response.status !== 200) {
    let detail: string | undefined;
    try {
      const json = await response.json();
      detail = typeof json?.detail === 'string' ? json.detail : undefined;
    } catch {
      // ignore parse error
    }
    throw new Error(detail || `Request failed with status ${response.status}`);
  }
  return (await response.json()) as ApiEnvelope<TData>;
}

interface RawTestConnectionData {
  external_org_name: string | null;
  custom_field_count: number;
}

interface RawConnectData {
  integration_id: string;
  external_org_name: string | null;
  enabled: boolean;
}

interface RawRotateKeyData {
  external_org_name: string | null;
  api_key_last_four: string | null;
  org_changed: boolean;
}

interface RawEnableDisableData {
  enabled: boolean;
  last_sync_status: string | null;
}

export const subcontractorHubService = {
  async testConnection(req: TestConnectionRequest): Promise<TestConnectionResponse> {
    const env = await postJson<RawTestConnectionData>(
      '/api/v1/integrations/subcontractorhub/test-connection',
      { api_key: req.apiKey },
    );
    if (env.data) {
      return {
        data: {
          externalOrgName: env.data.external_org_name,
          customFieldCount: env.data.custom_field_count,
        },
        error: null,
      };
    }
    if (env.error) {
      const code = env.error.code === 'auth_failed' ? 'auth_failed' : 'connection_test_failed';
      return { data: null, error: { code, message: env.error.message, details: env.error.details } };
    }
    return { data: null, error: { code: 'connection_test_failed', message: 'Unknown error' } };
  },

  async connect(req: ConnectRequest): Promise<ConnectResponse> {
    const env = await postJson<RawConnectData>(
      '/api/v1/integrations/subcontractorhub/connect',
      { api_key: req.apiKey, external_org_name: req.externalOrgName },
    );
    if (env.data) {
      return {
        data: {
          integrationId: env.data.integration_id,
          externalOrgName: env.data.external_org_name,
          enabled: false,
        },
        error: null,
      };
    }
    if (env.error) {
      const knownCodes = ['auth_failed', 'connection_test_failed', 'integration_already_exists'] as const;
      type KnownCode = (typeof knownCodes)[number];
      const code = (knownCodes as readonly string[]).includes(env.error.code)
        ? (env.error.code as KnownCode)
        : 'internal_error';
      return {
        data: null,
        error: { code, message: env.error.message, details: env.error.details },
      };
    }
    return { data: null, error: { code: 'internal_error', message: 'Unknown error' } };
  },

  async rotateKey(req: RotateKeyRequest): Promise<RotateKeyResponse> {
    const env = await postJson<RawRotateKeyData>(
      '/api/v1/integrations/subcontractorhub/rotate-key',
      { api_key: req.apiKey, confirm_org_change: req.confirmOrgChange ?? false },
    );
    if (env.data) {
      return {
        data: {
          externalOrgName: env.data.external_org_name,
          apiKeyLastFour: env.data.api_key_last_four,
          orgChanged: env.data.org_changed,
        },
        error: null,
      };
    }
    if (env.error) {
      const knownCodes = [
        'auth_failed',
        'connection_test_failed',
        'integration_not_found',
        'internal_error',
      ] as const;
      type KnownCode = (typeof knownCodes)[number];
      const code = (knownCodes as readonly string[]).includes(env.error.code)
        ? (env.error.code as KnownCode)
        : 'internal_error';
      return {
        data: null,
        error: { code, message: env.error.message, details: env.error.details },
      };
    }
    return { data: null, error: { code: 'internal_error', message: 'Unknown error' } };
  },

  async disableIntegration(): Promise<EnableDisableResponse> {
    return _toggleEnabled('/api/v1/integrations/subcontractorhub/disable');
  },

  async enableIntegration(): Promise<EnableDisableResponse> {
    return _toggleEnabled('/api/v1/integrations/subcontractorhub/enable');
  },

  async reconnectIntegration(req: ReconnectRequest): Promise<ReconnectResponse> {
    const env = await postJson<{
      external_org_name: string;
      api_key_last_four: string;
      previous_external_org_name: string | null;
    }>(
      '/api/v1/integrations/subcontractorhub/reconnect',
      { api_key: req.apiKey, confirm_org_name: req.confirmOrgName },
    );
    if (env.data) {
      return {
        data: {
          externalOrgName: env.data.external_org_name,
          apiKeyLastFour: env.data.api_key_last_four,
          previousExternalOrgName: env.data.previous_external_org_name,
        },
        error: null,
      };
    }
    if (env.error) {
      const knownCodes = [
        'auth_failed',
        'connection_test_failed',
        'integration_not_found',
        'same_organization',
        'org_name_mismatch',
        'internal_error',
      ] as const;
      type KnownCode = (typeof knownCodes)[number];
      const code = (knownCodes as readonly string[]).includes(env.error.code)
        ? (env.error.code as KnownCode)
        : 'internal_error';
      return {
        data: null,
        error: { code, message: env.error.message, details: env.error.details },
      };
    }
    return { data: null, error: { code: 'internal_error', message: 'Unknown error' } };
  },

  async deleteIntegration(): Promise<DeleteIntegrationResponse> {
    const env = await postJson<{ integration_id: string }>(
      '/api/v1/integrations/subcontractorhub/delete',
      {},
    );
    if (env.data) {
      return {
        data: { integrationId: env.data.integration_id },
        error: null,
      };
    }
    if (env.error) {
      const knownCodes = ['integration_not_found', 'internal_error'] as const;
      type KnownCode = (typeof knownCodes)[number];
      const code = (knownCodes as readonly string[]).includes(env.error.code)
        ? (env.error.code as KnownCode)
        : 'internal_error';
      return {
        data: null,
        error: { code, message: env.error.message, details: env.error.details },
      };
    }
    return { data: null, error: { code: 'internal_error', message: 'Unknown error' } };
  },

  async getIntegration(tenantId: string): Promise<SubContractorHubIntegration | null> {
    // AR21 defense-in-depth: filter by tenant_id at the application layer.
    const { data, error } = await supabase
      .from('tenant_integrations')
      .select(
        'id, tenant_id, integration_type, enabled, api_key_last_four, external_org_name, last_sync_at, last_sync_status, consecutive_auth_failures, default_landing_stage_id, fallback_owner_user_id, scheduled_sync_enabled, polling_interval_minutes, next_scheduled_sync_at, last_scheduled_sync_at, last_schema_drift_at, last_run_project_count, consecutive_scheduled_failures, auto_pause_threshold, auto_paused_at, created_at, updated_at',
      )
      .eq('tenant_id', tenantId)
      .eq('integration_type', 'subcontractorhub')
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    return {
      id: data.id,
      tenantId: data.tenant_id,
      integrationType: data.integration_type as 'subcontractorhub',
      enabled: !!data.enabled,
      apiKeyLastFour: data.api_key_last_four,
      externalOrgName: data.external_org_name,
      lastSyncAt: data.last_sync_at ?? null,
      lastSyncStatus: data.last_sync_status ?? null,
      consecutiveAuthFailures: data.consecutive_auth_failures ?? 0,
      defaultLandingStageId: data.default_landing_stage_id ?? null,
      fallbackOwnerUserId: data.fallback_owner_user_id ?? null,
      // SCH-4.1: scheduled sync config
      scheduledSyncEnabled: !!data.scheduled_sync_enabled,
      pollingIntervalMinutes: data.polling_interval_minutes ?? 60,
      nextScheduledSyncAt: data.next_scheduled_sync_at ?? null,
      lastScheduledSyncAt: data.last_scheduled_sync_at ?? null,
      // SCH-5.3: drift signal for SyncStatusCard badge. Non-null = drift
      // currently detected; cleared on the next discover-catalog call where
      // fingerprints match.
      lastSchemaDriftAt: data.last_schema_drift_at ?? null,
      // SCH-5.5: project-count baseline. Used by SyncStatusCard's
      // paused_drop_detected state caption + the resume-after-drop flow.
      lastRunProjectCount: data.last_run_project_count ?? null,
      // SCH-5.7: consecutive-failure auto-pause state.
      consecutiveScheduledFailures: data.consecutive_scheduled_failures ?? 0,
      autoPauseThreshold: data.auto_pause_threshold ?? 3,
      autoPausedAt: data.auto_paused_at ?? null,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  /**
   * SCH-3.5: update the integration's fallback owner.
   * Direct supabase update — RLS allows authenticated tenant members
   * to update tenant_integrations rows in their tenant.
   */
  async updateFallbackOwner(
    integrationId: string,
    userId: string | null,
  ): Promise<void> {
    const { error } = await supabase
      .from('tenant_integrations')
      .update({ fallback_owner_user_id: userId })
      .eq('id', integrationId);
    if (error) throw error;
  },

  /**
   * SCH-4.1: update scheduled-sync config (enable toggle + interval).
   * Calls the backend PATCH endpoint so the backend handles the enable-
   * transition timestamp computation (sets next_scheduled_sync_at = now +
   * interval) atomically with the row update.
   *
   * Returns the updated config (or throws on transport-level failures).
   */
  async updateScheduledSync(input: {
    enabled?: boolean;
    polling_interval_minutes?: number;
    // SCH-5.7: consecutive-failure auto-pause threshold (1-20).
    auto_pause_threshold?: number;
  }): Promise<{
    integration_id: string;
    scheduled_sync_enabled: boolean;
    polling_interval_minutes: number;
    next_scheduled_sync_at: string | null;
    last_scheduled_sync_at: string | null;
    auto_pause_threshold?: number;
  }> {
    const env = await patchJson<{
      integration_id: string;
      scheduled_sync_enabled: boolean;
      polling_interval_minutes: number;
      next_scheduled_sync_at: string | null;
      last_scheduled_sync_at: string | null;
      auto_pause_threshold?: number;
      no_change?: boolean;
    }>('/api/v1/integrations/subcontractorhub/scheduled-sync', input);
    if (env.error || !env.data) {
      throw new Error(env.error?.message || 'Failed to update scheduled sync');
    }
    return env.data;
  },

  /**
   * SCH-5.7: resume a sync that auto-paused after consecutive scheduled-sync
   * failures. Resets the failure counter + clears auto_paused_at and flips
   * lastSyncStatus back to 'active'. The admin is asserting they've fixed the
   * root cause; the next scheduled tick re-attempts.
   */
  async resumeAfterFailures(): Promise<
    { data: { success: true }; error: null } |
    { data: null; error: { code: string; message: string } }
  > {
    const env = await postJson<{ success: true }>(
      '/api/v1/integrations/subcontractorhub/resume-after-failures',
      {},
    );
    if (env.data) {
      return { data: env.data, error: null };
    }
    return {
      data: null,
      error: env.error ?? { code: 'internal_error', message: 'Unknown error' },
    };
  },

  /**
   * SCH-5.5: resume a sync that was paused by the project-count-drop
   * detector. Re-baselines `last_run_project_count` to the current SCH
   * total so the next sync doesn't immediately re-trip on the same drop.
   * Returns the new baseline; on success the integration's
   * lastSyncStatus flips back to 'active'.
   */
  async resumeAfterDrop(): Promise<
    { data: { success: true; new_baseline: number | null }; error: null } |
    { data: null; error: { code: string; message: string } }
  > {
    const env = await postJson<{ success: true; new_baseline: number | null }>(
      '/api/v1/integrations/subcontractorhub/resume-after-drop',
      {},
    );
    if (env.data) {
      return { data: env.data, error: null };
    }
    return {
      data: null,
      error: env.error ?? { code: 'internal_error', message: 'Unknown error' },
    };
  },
};
